/**
 * Liner webhook presets: CloudEvents 1.0 only (`specversion`, `id`, `source`, `type`, `time`, `datacontenttype`, `data`).
 * All samples use Maersk / DCSA Booking 2.0: `type` org.dcsa.booking.v2, `source` /maersk/ocean/booking/v2.
 */

export type LinerWebhookPreset = {
  id: string;
  label: string;
  /** Shown in UI as hint */
  description?: string;
  defaults: {
    lsaBookingRef: string;
    linerCode: string;
    subject: string;
  };
  payload: Record<string, unknown>;
};

export type LinerWebhookMergeFields = {
  lsaBookingRef: string;
  linerCode: string;
  cloudEventId: string;
  subject: string;
  isoTime: string;
};

const SAMPLE_LSA = "LSABK-20260321-SAMPLE";
const BKREQ_ORIG = `BKREQ-ORIG-${SAMPLE_LSA}`;
const BKREQ_AMEND = `BKREQ-AMEND-${SAMPLE_LSA}`;
const MOCK_CARRIER_REF = "MAEU979531000";

const MAERSK_BOOKING_V2_SOURCE = "/maersk/ocean/booking/v2";
const MAERSK_BOOKING_V2_TYPE = "org.dcsa.booking.v2";

/** Full-option `data.booking` (initial + amendment-confirmed samples). */
const maerskFullOptionBooking = {
  receiptTypeAtOrigin: "CY",
  deliveryTypeAtDestination: "SD",
  serviceContractReference: "SC-998877",
  transportPlan: [
    {
      vesselName: "MAERSK SURAT",
      vesselIMONumber: "9624275",
      carrierExportVoyageNumber: "412W",
      loadLocation: { locationName: "Jebel Ali", unLocationCode: "AEJEA" },
      dischargeLocation: { locationName: "Felixstowe", unLocationCode: "GBFXT" },
      expectedDepartureDate: "2026-04-10",
      expectedArrivalDate: "2026-05-05",
    },
  ],
  requestedEquipment: [
    {
      equipmentType: "20GP",
      requestedQuantity: 1,
      commodities: [
        {
          commodityType: "General Cargo",
          cargoGrossWeight: 12000,
          cargoGrossWeightUnit: "KGM",
        },
      ],
    },
  ],
};

const maerskFullOptionBookingAmendedVoyage = {
  ...maerskFullOptionBooking,
  transportPlan: [
    {
      ...(maerskFullOptionBooking.transportPlan[0] as Record<string, unknown>),
      carrierExportVoyageNumber: "605S",
    },
  ],
};

/** Deep-merge preset + editable fields + ISO time on the CloudEvents envelope. */
export function applyLinerMergeFields(
  payload: Record<string, unknown>,
  m: LinerWebhookMergeFields
): Record<string, unknown> {
  const out = structuredClone(payload) as Record<string, unknown>;

  out.id = m.cloudEventId;
  out.time = m.isoTime;
  if (m.lsaBookingRef) out.lsaBookingRef = m.lsaBookingRef;
  if (m.linerCode) out.linerCode = m.linerCode;
  if (m.subject) out.subject = m.subject;
  const data = out.data as Record<string, unknown>;
  if (data && typeof data === "object") {
    if (m.lsaBookingRef) data.lsaBookingRef = m.lsaBookingRef;
    if (m.linerCode) data.linerCode = m.linerCode;
  }

  return out;
}

export const LINER_WEBHOOK_PRESETS: LinerWebhookPreset[] = [
  {
    id: "maersk_booking_v2_initial_confirmed",
    label: "Booking confirmed (initial, full option)",
    description:
      "First-time CONFIRMED: amendmentStatus null, data.booking with transportPlan + requestedEquipment.",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "conf-8822-9911-aabb",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      time: "2026-03-21T21:45:00.000Z",
      datacontenttype: "application/json",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "CONFIRMED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        carrierBookingRequestReference: BKREQ_ORIG,
        bookingStatus: "CONFIRMED",
        amendmentStatus: null,
        updatedDateTime: "2026-03-21T21:44:00.000Z",
        booking: maerskFullOptionBooking,
      },
    },
  },
  {
    id: "maersk_booking_v2_initial_rejected",
    label: "Booking rejected (initial request)",
    description: "Carrier rejected the new booking: bookingStatus REJECTED, amendmentStatus null, reason.",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "00000000-0000-4000-8000-000000000001",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      time: "2026-03-21T12:00:00.000Z",
      datacontenttype: "application/json",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "REJECTED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        carrierBookingRequestReference: BKREQ_ORIG,
        bookingStatus: "REJECTED",
        amendmentStatus: null,
        reason: "Simulated rejection: invalid equipment or routing for this service.",
        updatedDateTime: "2026-03-21T11:59:00.000Z",
      },
    },
  },
  {
    id: "maersk_booking_v2_amendment_confirmed",
    label: "Amendment confirmed",
    description: "PUT/amend accepted: amendmentStatus AMENDMENT_CONFIRMED; carrierBookingRequestReference = amend ref.",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "00000000-0000-4000-8000-000000000002",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      time: "2026-03-21T14:00:00.000Z",
      datacontenttype: "application/json",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "CONFIRMED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        carrierBookingRequestReference: BKREQ_AMEND,
        bookingStatus: "CONFIRMED",
        amendmentStatus: "AMENDMENT_CONFIRMED",
        updatedDateTime: "2026-03-21T13:58:00.000Z",
        booking: maerskFullOptionBookingAmendedVoyage,
      },
    },
  },
  {
    id: "maersk_booking_v2_amend_declined",
    label: "Amendment declined",
    description: "Booking stays CONFIRMED; amendmentStatus AMENDMENT_DECLINED + metadata.",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "00000000-0000-4000-8000-000000000003",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      datacontenttype: "application/json",
      time: "2026-03-21T20:30:00.000Z",
      subscriptionReference: "sub-abc-123-xyz",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "CONFIRMED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        carrierBookingRequestReference: BKREQ_AMEND,
        bookingStatus: "CONFIRMED",
        amendmentStatus: "AMENDMENT_DECLINED",
        reason: "Vessel space unavailable for the requested change in container quantity.",
        updatedDateTime: "2026-03-21T20:29:55.000Z",
        metadata: {
          vesselName: "MAERSK SENTOSA",
          voyageNumber: "601N",
        },
      },
    },
  },
  {
    id: "maersk_booking_v2_cancel_confirmed",
    label: "Cancellation accepted",
    description: "Carrier accepted cancel: bookingStatus CANCELLED, amendmentStatus null (DELETE webhook shape).",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "00000000-0000-4000-8000-000000000004",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      time: "2026-03-21T22:10:00.000Z",
      datacontenttype: "application/json",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "CANCELLED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        bookingStatus: "CANCELLED",
        amendmentStatus: null,
        reason: "Booking cancelled per customer request.",
        updatedDateTime: "2026-03-21T22:05:00.000Z",
      },
    },
  },
  {
    id: "maersk_booking_v2_cancel_declined",
    label: "Cancellation declined",
    description: "Carrier rejected cancel: booking stays CONFIRMED; amendmentStatus CANCEL_DECLINED.",
    defaults: {
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      subject: SAMPLE_LSA,
    },
    payload: {
      specversion: "1.0",
      id: "00000000-0000-4000-8000-000000000005",
      source: MAERSK_BOOKING_V2_SOURCE,
      type: MAERSK_BOOKING_V2_TYPE,
      time: "2026-03-21T22:15:00.000Z",
      datacontenttype: "application/json",
      lsaBookingRef: SAMPLE_LSA,
      linerCode: "MAEU",
      bookingStatus: "CONFIRMED",
      subject: SAMPLE_LSA,
      data: {
        carrierBookingReference: MOCK_CARRIER_REF,
        bookingStatus: "CONFIRMED",
        amendmentStatus: "CANCEL_DECLINED",
        reason: "Cancellation rejected: Container is already loaded on vessel MAERSK HANOI.",
        updatedDateTime: "2026-03-21T22:12:00.000Z",
      },
    },
  },
];

export function getLinerWebhookPreset(id: string): LinerWebhookPreset | undefined {
  return LINER_WEBHOOK_PRESETS.find((p) => p.id === id);
}
